import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { ModalLoginTermsComponent } from '../../components/modal-login-terms/modal-login-terms.component';
import { AuthService } from '../../services/auth.service';
import { OpenModalConfirmService } from '../../services/open-modal-confirm.service';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit {
  readonly router = inject(Router);
  readonly authService = inject(AuthService);
  readonly fb = inject(FormBuilder);
  readonly openModalConfirmService = inject(OpenModalConfirmService);
  private dialog = inject(MatDialog);

  isForgotPassword = false;
  incorrectPassword: boolean = false;
  isCorrectingPassword: boolean = false;
  isConfirmingEmail: boolean = false;
  isConfirmedPassword: boolean = false;
  intervalId: any;
  currentImageIndex = 0;
  usernameFocused: boolean = false;
  passwordFocused: boolean = false;
  showPassword: boolean = false;
  isLogin: boolean = true;

  isChangePassword: boolean = false;

  resetPasswordForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });

  changePasswordForm = this.fb.group({
    newPassword: ['', [
      Validators.required,
      Validators.minLength(8),
      this.passwordStrengthValidator,
    ]],
    confirmNewPassword: ['', Validators.required],
    oldPassword: [''],
    username: [''],
  }, {
    validators: this.passwordsMatchValidator, // Validador de igualdade
  });

  images = [
    '../../../assets/head-office-background.png',
    '../../../assets/trucks-background.png',
    '../../../assets/head-office2-background.png',
    '../../../assets/trucks2-background.png',
    '../../../assets/login-employee-background.png',
  ];
  loginForm: FormGroup = this.fb.group({
    username: ['', Validators.required],
    password: ['', Validators.required],
  });

  ngOnInit() {
    this.startCarousel();
    sessionStorage.setItem('systemType', 'MASTERBOI');
  }

  startCarousel() {
    setInterval(() => {
      this.nextImage();
    }, 3000);
  }

  nextImage() {
    this.currentImageIndex = (this.currentImageIndex + 1) % this.images.length;
  }

  setImage(index: number) {
    this.currentImageIndex = index;
  }

  getImages(): NodeListOf<HTMLImageElement> {
    return document.querySelectorAll('.carousel-images img');
  }

  getRadios(): NodeListOf<HTMLInputElement> {
    return document.querySelectorAll('.radio-container input');
  }

  submitLoginForm(): void {
    this.incorrectPassword = false;
    if (this.loginForm.valid) {
      const formValues = this.loginForm.value;
      this.authService.login(formValues.username!, formValues.password!).subscribe((res) => {
        const token = res;
        this.authService.storeDatas(token);
        const userItem = sessionStorage.getItem('user');
        const user = userItem ? JSON.parse(userItem) : {};
        if (user.authorities.some((item: string) => item === 'ROLE_SAFETRACE_SUPPORT')) {
          sessionStorage.setItem('systemType', 'SAFETRACE');
          this.router.navigate(['/support-panel']);
        } else if (user.authorities.some((item: string) => item === 'ROLE_SUPERADMIN' || item === 'ROLE_MASTERBOI_ADMIN')) {
          sessionStorage.setItem('systemType', 'MASTERBOI');
          this.router.navigate(['/panel/']);
        } else if (user.authorities.some((item: string) => item === 'ROLE_MASTERBOI_PROVIDER' || item === 'ROLE_USER')) {
          sessionStorage.setItem('systemType', 'MASTERBOI');
          if (!user.disclaimerAccepted) {
            this.dialog.open(ModalLoginTermsComponent);
          } else {
            if (!user.confirmPersonalData) {
              this.router.navigate(['/client-panel/home']);
            } else {
              this.router.navigate(['/client-panel/consent-form']);
            }
          }
        } else {
          this.incorrectPassword = true;
        }
      },
        (error) => {
          if (error && error.status === 'CHANGE_PASSWORD') {
            this.isForgotPassword = false;
            this.isLogin = false;
            this.isChangePassword = true;
          } else {
            this.incorrectPassword = true;
          }
        }
      );
    } else {
      this.incorrectPassword = true;
    }
  }

  passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmNewPassword')?.value;
    return password === confirmPassword ? null : { passwordsMismatch: true };
  }

  onFocus(input: string) {
    if (input === 'username') {
      this.usernameFocused = true;
      this.onBlur('password');
    } else if (input === 'password') {
      this.passwordFocused = true;
    }
  }

  onBlur(input: string) {
    if (input === 'username') {
      this.usernameFocused = false;
    } else if (input === 'password') {
      this.passwordFocused = false;
    }
  }

  passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const value: string = control.value || '';

    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

    const isValid =
      value.length >= 8 &&
      hasUpperCase &&
      hasLowerCase &&
      hasNumber &&
      hasSpecialChar;

    return isValid ? null : { passwordStrength: true };
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  handleForgetPassword(): void {
    this.isForgotPassword = true;
    this.isLogin = false;
    this.isConfirmingEmail = true;
  }

  submitEmailCode(): void {
    this.isCorrectingPassword = true;
    this.isConfirmingEmail = false;
  }

  handleSubmitChangePassword() {
    this.changePasswordForm.markAllAsTouched();
    if (this.changePasswordForm.valid) {
      const oldPassword = this.loginForm.get('password')?.value;
      const username = this.loginForm.get('username')?.value;
      this.changePasswordForm.get('oldPassword')?.setValue(oldPassword);
      this.changePasswordForm.get('username')?.setValue(username);
      if (this.changePasswordForm.valid) {
        const username = this.changePasswordForm.get('username')?.value as string;
        const payload = {
          username: username.trim(),
          oldPassword: this.changePasswordForm.get('oldPassword')?.value?.trim() as string,
          newPassword: this.changePasswordForm.get('newPassword')?.value?.trim() as string,
        };
        this.authService.changePassword(payload).subscribe({
          next: (res) => {
            this.loginForm.get('password')?.setValue(this.changePasswordForm.get('newPassword')?.value);
            this.submitLoginForm();
          },
          error: (error) => { },
        });
      }
    }
  }

  submitNewPassword(): void {
    this.isCorrectingPassword = false;
    this.isConfirmingEmail = false;
    this.isConfirmedPassword = true;
  }

  handleInitialLoginPage(): void {
    this.isForgotPassword = false;
    this.isChangePassword = false;
    this.isLogin = true;
  }

  handleClickResetPassword(): void {
    if (this.resetPasswordForm.valid) {
      const email = this.resetPasswordForm.get('email')?.value as string;

      this.authService.sendEmailResetPassword(email.trim()).subscribe({
        next: () => {
          this.openModalConfirmService.openModalConfirm({
            text: 'E-mail enviado com sucesso!',
            subText: 'Confira sua caixa de entrada ou pasta de spam.',
            type: 'success',
            confirmText: 'Ok',
            hideCancelButton: true,
          }).subscribe(() => {
            this.handleInitialLoginPage();
          });
        },
        error: () => {
          this.openModalConfirmService.openModalConfirm({
            text: 'Erro ao enviar e-mail!',
            subText: 'Tente novamente mais tarde.',
            type: 'error',
            confirmText: 'Ok',
            hideCancelButton: true,
          }).subscribe();
        }
      });
    }
  }
}
